<script lang="ts" setup>
import { computed, ref, watch, onUnmounted, onMounted } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  ItemDefinition,
  TimeUnit,
  Trigger,
  ActionType,
  TriggerType,
} from "~/api";
import { showError } from "~/utils/handleAPIError";
import { apiClient } from "~/utils/apiClient";
import { CopyDocument, Lock, Unlock, Delete } from "@element-plus/icons-vue";
import { Slotted } from "~/types/Slotted";

import ItemDefinitionStatuses from "./ItemDefinitionStatuses.vue";
import { ComplianceStatus, ComplianceCategory } from "~/types/ComplianceTypes";
import { enumDescriptions } from "~/utils/enumDescriptions";
import EnumTag from "~/components/EnumTag.vue";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { useStorage } from "@vueuse/core";
import { useEntityDefinitionStore } from "~/stores/EntityDefinitionStore";
import { plural } from "~/utils/miscUtils";
import { emptyGuid } from "~/utils/constants";

const entityDefinitionStore = useEntityDefinitionStore();

const myInfoStore = useMyInfoStore();

const itemDefinition = defineModel<ItemDefinition>();
const editing = ref(true);

const emit = defineEmits<{
  (e: "closeDrawer"): void;
  (e: "updated", value: ItemDefinition): void;
  (e: "added", value: ItemDefinition): void;
  (e: "deleted", value: ItemDefinition): void;
}>();
const publicItem = ref<boolean>();
const globalItem = ref<boolean>();
const copiedItemStore = useStorage("editing-copied-item", false, localStorage);

watch(
  () => itemDefinition.value, // trigger when loaded
  () => {
    editing.value =
      !itemDefinition.value?.guid || copiedItemStore.value ? true : false;
    publicItem.value = itemDefinition.value?.madePublicAt ? true : false;
    globalItem.value = itemDefinition.value?.madeGlobalAt ? true : false;
  },
);

const isUsable = computed(() => {
  return itemDefinition.value?.guid !== emptyGuid;
});
const triggerValue = ref<number>();
const triggerActionType = ref<ActionType>();
const triggerTimeUnit = ref<TimeUnit>();
const triggerType = ref<TriggerType>();

const saveItem = async () => {
  if (!itemDefinition.value) {
    return;
  }
  const guid = itemDefinition.value.guid;
  if (!guid || guid === emptyGuid) {
    itemDefinition.value.sysOrgGuid = myInfoStore.currentSysOrgGuid;
    if (!itemDefinition.value.name) {
      showError("Item name is required");
      return;
    }
    if (!itemDefinition.value.forEntityDefinitionGuid) {
      showError("Entity is required");
      return;
    }
    await apiClient.itemDefinition
      .createItemDefinition(itemDefinition.value)
      .then((res) => {
        ElMessage.success("Saved");
        emit("added", res);
        // handleCloseDrawer();
      })
      .catch(showError);
  } else {
    await apiClient.itemDefinition
      // otherwise, we just update it
      .updateItemDefinition(guid, itemDefinition.value)
      .then((res) => {
        ElMessage.success("Saved");
        copiedItemStore.value = false;
        emit("updated", res);
      })
      .catch(showError);
  }
};

const handleUpdateItemPrivacy = async (val: string | number | boolean) => {
  await ElMessageBox.confirm(
    `This will make this item ${publicItem.value ? "visible to everyone" : "hidden from everyone outside your organization"}. Continue?`,
    `Make Item ${publicItem.value ? "Public" : "Private"}`,
    {
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    },
  )
    .then(async () => {
      if (!itemDefinition.value) {
        showError("Compliance item not found");
        return;
      }
      if (!itemDefinition.value) {
        showError("Compliance item not found");
        return;
      }
      if (val) {
        itemDefinition.value.madePublicAt = new Date().toISOString();
      } else {
        itemDefinition.value.madePublicAt = undefined;
      }
      await saveItem();
    })
    .catch(() => {
      publicItem.value = !publicItem.value;
      ElMessage({
        type: "info",
        message: "Canceled",
      });
    });
};

const numChecklists = computed(() => {
  return itemDefinition.value?.itemsInChecklists?.length ?? 0;
});

const handleUpdateItemGlobal = async (val: boolean) => {
  if (!itemDefinition.value) {
    showError("Compliance item not found");
    return;
  }
  if (val) {
    itemDefinition.value.madeGlobalAt = new Date().toISOString();
  } else {
    itemDefinition.value.madeGlobalAt = undefined;
  }
};

const handleAddTrigger = async () => {
  if (!itemDefinition.value) {
    showError("Compliance item not found");
    return;
  }
  if (
    !triggerValue.value ||
    !triggerTimeUnit.value ||
    !triggerType.value ||
    !triggerActionType.value
  ) {
    showError("Please fill in all fields");
    return;
  }
  itemDefinition.value.triggers.push({
    action: triggerActionType.value,
    value: triggerValue.value,
    unit: triggerTimeUnit.value,
    type: triggerType.value,
    isRecurring: false,
  });

  triggerActionType.value = undefined;
  triggerValue.value = undefined;
  triggerTimeUnit.value = undefined;
  triggerType.value = undefined;
};

const handleMakeCopy = async () => {
  if (!itemDefinition.value) {
    showError("Compliance item not found");
    return;
  }
  const newItem = { ...itemDefinition.value };

  if (!newItem.formDefinition) {
    showError("Compliance item doesn't have a form definition");
    return;
  }
  // @ts-expect-error - we want guid to be set by the server
  newItem.guid = undefined;
  newItem.name = `${newItem.name} (Copy)`;
  newItem.madePublicAt = undefined;
  // @ts-expect-error - we want guid to be set by the server
  newItem.formDefinition.guid = undefined;
  newItem.formDefinition?.components.forEach((c) => {
    // @ts-expect-error - we want guid to be set by the server
    c.guid = undefined;
  });

  await apiClient.itemDefinition
    .createItemDefinition(newItem)
    .then((res) => {
      ElMessage.success("Copied");
      copiedItemStore.value = true;
      emit("added", res);
    })
    .catch(showError);
};

const handleDeleteItemDefinition = async (
  itemDefinitionGuid: string,
  sysOrgGuid: string,
) => {
  ElMessageBox.confirm(
    "Are you sure you want to delete this item definition?",
    "Delete Item Definition",
    {
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    },
  )
    .then(async () => {
      if (!itemDefinition.value) {
        return;
      }

      await apiClient.itemDefinition
        .deleteItemDefinition(itemDefinitionGuid, sysOrgGuid)
        .then((res) => {
          ElMessage.success("Item definition deleted");
          emit("closeDrawer");
          emit("deleted", res);
        })
        .catch(showError);
    })
    .catch(async () => {
      // do nothing
    });
};

onMounted(async () => {
  await Promise.all([entityDefinitionStore.load(), myInfoStore.load()]);
  localStorage.setItem("editing-copied-item", JSON.stringify(false));
});

onUnmounted(() => {
  localStorage.setItem("editing-copied-item", JSON.stringify(false));
});
</script>
<template>
  <div v-if="myInfoStore.hasSetupBasicRole" class="edit-buttons">
    <el-button v-if="!editing" type="success" @click="editing = true">
      Edit
    </el-button>

    <el-button v-if="editing" plain type="danger" @click="editing = false"
      >Cancel</el-button
    >
    <el-button v-if="editing" type="success" @click="saveItem">Save</el-button>
  </div>
  <br />
  <br />
  <div v-if="itemDefinition" class="compliance-item-details">
    <section class="section core">
      <table>
        <tbody>
          <tr>
            <td>Item Name</td>
            <td>
              <el-input
                v-if="editing"
                v-model="itemDefinition.name"
                :autofocus="copiedItemStore"
              />
              <span v-else>{{ itemDefinition.name }}</span>
            </td>
          </tr>
          <tr>
            <td>Entity Type</td>
            <td>
              <el-select
                v-if="editing"
                v-model="itemDefinition.forEntityDefinitionGuid"
                filterable
              >
                <el-option
                  v-for="entity in entityDefinitionStore.EntitiesDefinitionList"
                  :key="entity.guid"
                  :label="entity.name ?? undefined"
                  :value="entity.guid"
                />
              </el-select>
              <span v-else>{{
                entityDefinitionStore.nameOfType(
                  itemDefinition.forEntityDefinitionGuid,
                )
              }}</span>
            </td>
          </tr>
          <tr>
            <td>Purpose</td>
            <td>
              <el-input
                v-if="editing"
                v-model="itemDefinition.purposes"
              ></el-input>
              <span v-else>{{ itemDefinition.purposes }}</span>
            </td>
          </tr>

          <tr>
            <td>Item Status List</td>
            <td>
              <div v-if="!editing" class="statusList">
                <el-tag
                  v-for="status in itemDefinition.statusList"
                  :key="status.code ?? ''"
                >
                  {{ status.label }}
                  <i
                    v-if="status.isEndpoint"
                    :class="`mdi mdi-check-decagram`"
                  ></i>
                </el-tag>
              </div>
              <div v-else>
                <ItemDefinitionStatuses
                  :key="itemDefinition.statusList?.length"
                  :item-definition="itemDefinition"
                />
              </div>
            </td>
          </tr>
          <tr>
            <td>Importance</td>
            <td>
              <el-select
                v-if="editing"
                v-model="itemDefinition.importance"
                filterable
              >
                <el-option
                  v-for="(key, value) in ComplianceCategory"
                  :key="key"
                  :label="key"
                  :value="value"
                />
              </el-select>
              <span v-else>{{ itemDefinition.importance }}</span>
            </td>
          </tr>
          <tr>
            <td>
              Only One Per
              {{
                entityDefinitionStore.nameOfType(
                  itemDefinition.forEntityDefinitionGuid,
                )
              }}?
            </td>
            <td>
              <el-checkbox
                v-if="editing"
                v-model="globalItem"
                :active-action-icon="Unlock"
                :inactive-action-icon="Lock"
                @change="handleUpdateItemGlobal"
              />
              <span v-else>
                <el-icon v-if="itemDefinition.madeGlobalAt"
                  ><el-icon-check
                /></el-icon>
                <el-icon v-else><el-icon-close /></el-icon>
              </span>
            </td>
          </tr>
          <tr>
            <td>
              Make Item Public
              <el-tooltip
                content="Other organizations on this website will be able to use this item definition"
                placement="top"
              >
                <el-icon>
                  <el-icon-info-filled />
                </el-icon>
              </el-tooltip>
            </td>
            <td>
              <el-checkbox
                v-if="isUsable && editing"
                v-model="publicItem"
                @change="
                  async ($event: boolean) =>
                    await handleUpdateItemPrivacy($event)
                "
              >
                <span>{{}}</span>
              </el-checkbox>
              <span v-else>
                <el-icon v-if="itemDefinition.madePublicAt"
                  ><el-icon-check
                /></el-icon>
                <el-icon v-else><el-icon-close /></el-icon>
              </span>
            </td>
          </tr>

          <tr>
            <td>
              Default Expiry
              <el-tooltip
                content="This is the default expiry date for this item. It will be used if no expiry date is set via the form."
                placement="top"
              >
                <el-icon>
                  <el-icon-info-filled />
                </el-icon>
              </el-tooltip>
            </td>
            <td class="expiry-date">
              <div v-if="!editing">
                <span v-if="itemDefinition.defaultExpiry">
                  {{ itemDefinition.defaultExpiry.value }}
                  {{ itemDefinition.defaultExpiry.unit.toLowerCase() }}
                  <span>after creation</span>
                </span>
                <span v-else>-</span>
              </div>
              <div v-else>
                <div v-if="itemDefinition.defaultExpiry" class="expiry-date">
                  <el-input-number
                    v-model="itemDefinition.defaultExpiry.value"
                    placeholder="2"
                    :controls="false"
                    :disabled="!editing"
                  />
                  <el-select
                    v-model="itemDefinition.defaultExpiry.unit"
                    placeholder="Days"
                    :disabled="!editing"
                    filterable
                  >
                    <el-option
                      v-for="(description, type) in enumDescriptions.timeUnit"
                      :key="type"
                      :label="description.label"
                      :value="type"
                    />
                  </el-select>
                  <span>after creation</span>
                  <el-button
                    type="danger"
                    :icon="Delete"
                    circle
                    @click="itemDefinition.defaultExpiry = undefined"
                  />
                </div>
                <div v-else>
                  <el-button
                    v-if="editing"
                    type="info"
                    @click="
                      itemDefinition.defaultExpiry = {
                        value: 1,
                        unit: TimeUnit.DAY,
                      }
                    "
                  >
                    Set Default Expiry
                  </el-button>
                </div>
              </div>
            </td>
          </tr>
          <tr>
            <td>Default Onboarding Form</td>
            <!-- <td>{{ itemDefinition.defaultOnboardingFormGuid ?? "-" }}</td> -->
            <td>-</td>
          </tr>
          <tr>
            <td>Default Maintenance Form</td>
            <!-- <td>{{ itemDefinition.defaultMaintenanceFormGuid ?? "-" }}</td> -->
            <td>-</td>
          </tr>
          <tr>
            <td>Definition Status</td>
            <td>
              <el-select
                v-if="editing"
                v-model="itemDefinition.status"
                filterable
              >
                <el-option
                  v-for="(key, value) in ComplianceStatus"
                  :key="key"
                  :label="key"
                  :value="value"
                />
              </el-select>

              <span v-else>
                <EnumTag
                  :model-value="itemDefinition.status"
                  size="large"
                  type="complianceItemStatus"
                />
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
    <section class="sections triggers">
      <h3>Triggers</h3>
      <div v-if="editing" class="edit-triggers">
        <el-select
          v-model="triggerActionType"
          placeholder="Notification"
          filterable
        >
          <el-option
            v-for="(description, type) in enumDescriptions.actionType"
            :key="type"
            :label="description.label"
            :value="type"
          />
        </el-select>
        <el-input-number
          v-model="triggerValue"
          placeholder="2"
          :controls="false"
        />
        <el-select v-model="triggerTimeUnit" placeholder="Days" filterable>
          <el-option
            v-for="(description, type) in enumDescriptions.timeUnit"
            :key="type"
            :label="description.label"
            :value="type"
          />
        </el-select>
        <el-select v-model="triggerType" placeholder="Before" filterable>
          <el-option
            v-for="(description, type) in enumDescriptions.triggerType"
            :key="type"
            :label="description.label"
            :value="type"
          />
        </el-select>
        <el-button type="info" @click="handleAddTrigger">
          Add Trigger
        </el-button>
      </div>
      <h4>Fixed Triggers</h4>
      <el-table
        :data="itemDefinition.triggers?.filter((t) => !t.isRecurring)"
        style="width: 100%"
      >
        <el-table-column prop="action" label="Action">
          <template #default="{ row }: Slotted<Trigger>">
            <span v-if="row.action">
              {{ enumDescriptions.actionType[row.action].label }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="value" label="Number Of"></el-table-column>
        <el-table-column prop="unit" label="Day/Week/Month/Year">
          <template #default="{ row }: Slotted<Trigger>">
            <span v-if="row.unit"> {{ row.unit }}(s) </span>
          </template>
        </el-table-column>
        <el-table-column prop="type" label="Before/After"></el-table-column>
        <el-table-column v-if="editing" label="Delete">
          <template #default="{ row }: Slotted<Trigger>">
            <el-button
              type="danger"
              :icon="Delete"
              circle
              @click="
                itemDefinition.triggers.splice(
                  itemDefinition.triggers.indexOf(row),
                  1,
                )
              "
            />
          </template>
        </el-table-column>
      </el-table>
      <!-- TODO: handle recurring triggers in the future -->
      <!-- <el-table
        :data="itemDefinition.triggers.filter((t) => t.isRecurring)"
        style="width: 100%"
      >
        <el-table-column label="Recurring Triggers">
          <el-table-column prop="type" label="">
            <template #default="{ row }: Slotted<Trigger>">
              <span v-if="row.type">
                {{ enumDescriptions.triggerType[row.type] }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="unit" label="Unit"></el-table-column>
          <el-table-column prop="value" label="Value"></el-table-column>
        </el-table-column>
      </el-table> -->
    </section>
    <section class="section delete">
      <div>
        <el-button
          v-if="editing && itemDefinition.guid !== emptyGuid"
          :disabled="numChecklists > 0"
          type="danger"
          plain
          @click="
            handleDeleteItemDefinition(
              itemDefinition.guid,
              myInfoStore.currentSysOrgGuid,
            )
          "
          >Delete Item Definition</el-button
        >
        <div v-if="numChecklists">
          This item is used in
          {{ numChecklists === 1 ? "this" : "these" }} checklist{{
            plural(numChecklists)
          }}:
          <ul>
            <li
              v-for="(checklist, index) in itemDefinition.itemsInChecklists"
              :key="index"
            >
              {{ checklist.itemName }}
            </li>
          </ul>
        </div>
        <div v-else>This item is not used in any checklists.</div>
      </div>
    </section>

    <el-button
      v-if="!editing && itemDefinition.guid"
      class="copy-btn"
      :icon="CopyDocument"
      @click="handleMakeCopy"
    >
      Make Copy
    </el-button>
  </div>
</template>

<style lang="less">
.item-designer,
.el-tabs,
.el-tabs__content,
.el-tab-pane,
.compliance-item-details {
  height: 100%;
}
.compliance-item-details {
  display: flex;
  flex-direction: column;
  height: 90%;

  .edit-buttons {
    display: flex;
    .el-switch + .el-button {
      margin-left: 12px;
    }
  }

  .copy-btn {
    justify-self: end;
    align-self: end;
    margin-top: 1em;
  }

  .expiry-date {
    display: flex;
    align-items: center;
    gap: 1em;

    .el-input-number {
      width: 3em;
    }
    .el-select {
      width: 7em;
    }
  }

  .triggers {
    flex: 1;
    .el-table {
      margin-top: 1em;
    }

    .edit-triggers {
      display: flex;
      gap: 1em;

      .el-select {
        width: 10em;
      }
    }
  }

  .delete {
    flex: 2;
    margin-top: 20px;
  }

  table {
    td {
      padding: 0.5em;
      border-bottom: 1px solid #ebeef5;
    }
    td:first-child {
      white-space: nowrap;
    }
  }
}
</style>
